import { useMutation } from 'react-query';
import usePlanStore, {
  CalculatedBudget,
  YouTubeConfig,
  MetaConfig,
  TikTokConfig,
  CalculatedTVBudget,
  TVConfig,
} from 'store/planStore';
import { getCalculatedOneReach } from 'api/services/mediaPlanning/calculateOneReach';
import {
  OneReachBody,
  AutomaticOneReachTouchpoint,
  OneReachTouchpoint,
} from 'api/services/mediaPlanning/types';
import moment from 'moment/moment';
import { OOHBudget } from '../../../../../../api/services/mediaPlanning/offline/oohBudget';
import { useError } from '../../../common/Errors/ErrorContext/ErrorContext';
import getConfigSaturationNullish from '../utils/getConfigSaturationNullish';

const getTouchpointFunnelBudgets = (
  touchpointName: string,
  calculatedBudget: CalculatedBudget[]
) => {
  return calculatedBudget
    .filter(
      (item) =>
        item.touchpointName.toLowerCase().replace(' ', '') ===
        touchpointName.toLowerCase()
    )
    .map((item) => ({
      funnel: item.touchpointFunnel,
      budget: item.budget.value,
      oneReachCalculationVariants: item.oneReachCalculationVariants,
    }));
};

const getTVTouchpointFunnelBudgets = (
  touchpointName: string,
  calculatedBudget?: CalculatedTVBudget
) => {
  return {
    funnel: 'Awareness',
    budget:
      calculatedBudget?.copies?.reduce(
        (acc, copy) => acc + (copy.campaignBudget.value ?? 0),
        0
      ) ?? 0,
  };
};

const getOOHTouchpointFunnelBudgets = (calculatedBudget?: OOHBudget) => {
  return {
    funnel: 'Awareness',
    budget:
      calculatedBudget?.data?.reduce(
        (acc, row) => acc + (row.budget.value ?? 0),
        0
      ) ?? 0,
  };
};

const getTouchpointData = (
  touchpointName: string,
  calculatedBudget: CalculatedBudget[],
  config?: YouTubeConfig | MetaConfig | TikTokConfig
): OneReachTouchpoint | null => {
  const budgets = getTouchpointFunnelBudgets(touchpointName, calculatedBudget);

  const { targetGroup, saturation, saturationMin, saturationMax } =
    config || {};

  if (!budgets.length || !targetGroup) {
    return null;
  }

  return {
    touchpointFunnels: budgets,
    touchpointTargetGroup:
      typeof targetGroup === 'object' ? targetGroup.targetGroup : targetGroup,
    saturation,
    saturationMin,
    saturationMax,
  };
};

const getAutomaticTouchpointsData = (
  touchpointNames: readonly string[],
  calculatedBudget: CalculatedBudget[]
): Record<string, AutomaticOneReachTouchpoint> => {
  const automaticTouchpointsToProcess: Record<
    string,
    AutomaticOneReachTouchpoint
  > = {};

  touchpointNames.forEach((touchpoint) => {
    const budgets = getTouchpointFunnelBudgets(touchpoint, calculatedBudget);
    if (budgets.length) {
      automaticTouchpointsToProcess[touchpoint] = {
        touchpointFunnels: budgets,
      };
    }
  });

  return automaticTouchpointsToProcess;
};

const getTvTouchpointData = (
  targetGroup?: string,
  calculatedBudget?: CalculatedTVBudget,
  tvConfig?: TVConfig
):
  | (Partial<OneReachTouchpoint> & {
      grps?: {
        year: number;
        month: number;
        weekNumber: number;
      }[];
      totalGrp?: number;
    })
  | null => {
  const tvBudgets = getTVTouchpointFunnelBudgets('tv', calculatedBudget);
  const { weeklyWeight, grpTable } = tvConfig || {};

  const grps = grpTable?.weeks.map((week) => {
    const parsedDate = moment(week.startingDate, 'DD-MM-YYYY');
    return {
      year: parsedDate.year(),
      month: parsedDate.month() + 1,
      weekNumber: week.weekNumber,
    };
  });

  return {
    touchpointFunnels: [tvBudgets],
    touchpointTargetGroup: targetGroup,
    saturation: weeklyWeight?.current,
    saturationMin: weeklyWeight?.min,
    saturationMax: weeklyWeight?.max,
    totalGrp: tvConfig?.calculatedBudget?.copies?.reduce((sum, copy) => {
      return sum + (copy.totalGrp || 0);
    }, 0),
    grps,
  };
};

const getOOHTouchpointData = (
  calculatedBudget?: OOHBudget
): Partial<OneReachTouchpoint> | null => {
  const oohBudgets = getOOHTouchpointFunnelBudgets(calculatedBudget);

  return {
    touchpointFunnels: [oohBudgets],
  };
};

const useCalculateOneReach = () => {
  const {
    countryForms,
    selectedCountry,
    selectedCountryISOCode,
    savedCountryForms,
    setCountryOneReach,
  } = usePlanStore();

  const { showError } = useError();

  const {
    mutate: calculateOneReach,
    mutateAsync: asyncCalculateOneReach,
    data: response,
    isLoading,
    isError,
  } = useMutation(
    async () => {
      const updatedForm =
        usePlanStore.getState().storedCountryForms[selectedCountry!];
      const countryForm = selectedCountry ? updatedForm : null;
      const calculatedBudget = countryForm?.calculatedBudget || [];
      const calculatedTVBudget = countryForm?.offline?.tv?.calculatedBudget;
      const calculatedOOHBudget = countryForm?.offline?.ooh?.calculatedBudget;

      const calculatedBudgetIncludedInCalculations = calculatedBudget.filter(
        (budgetRow) => {
          return budgetRow.isIncludedInCalculations;
        }
      );

      const calculatedOOHBudgetIncludedInCalculations = {
        isRequired: !!calculatedOOHBudget?.isRequired,
        data:
          calculatedOOHBudget?.data.filter(
            (dataRow) => dataRow.isIncludedInCalculations
          ) ?? [],
      };

      const { tiktokConfig, metaConfig, youTubeConfig } = countryForm || {};
      if (
        !countryForm ||
        (!calculatedBudget.length &&
          !calculatedOOHBudget &&
          !calculatedTVBudget) ||
        !selectedCountryISOCode
      ) {
        return null;
      }

      const touchpoints: OneReachBody['touchpoints'] = {};

      const youtube = getTouchpointData(
        'youtube',
        calculatedBudgetIncludedInCalculations,
        youTubeConfig
      );
      if (youtube && !getConfigSaturationNullish(youTubeConfig)) {
        touchpoints.youtube = youtube;
      }

      const meta = getTouchpointData(
        'meta',
        calculatedBudgetIncludedInCalculations,
        metaConfig
      );
      if (meta && !getConfigSaturationNullish(metaConfig)) {
        touchpoints.meta = meta;
      }

      const tiktok = getTouchpointData(
        'tiktok',
        calculatedBudgetIncludedInCalculations,
        tiktokConfig
      );
      if (tiktok && !getConfigSaturationNullish(tiktokConfig)) {
        touchpoints.tiktok = tiktok;
      }

      if (countryForm.offline?.tv?.targetGroup) {
        const tv = getTvTouchpointData(
          countryForm.offline?.tv?.targetGroup,
          calculatedTVBudget,
          countryForm.offline?.tv
        );

        if (tv?.touchpointFunnels && countryForm.offline?.tv?.isAvailable) {
          touchpoints.tv = tv;
        }
      }

      const ooh = getOOHTouchpointData(
        calculatedOOHBudgetIncludedInCalculations
      );
      if (ooh?.touchpointFunnels && countryForm.offline?.ooh?.isAvailable) {
        touchpoints.ooh = ooh;
      }

      const automaticTouchpoints = [
        'snapchat',
        'pinterest',
        'programmaticDisplay',
      ] as const;

      const includedAutomaticTouchpoints = automaticTouchpoints.filter(
        (touchpointName) =>
          countryForm.calculatedBudget?.some((touchpoint) => {
            return (
              touchpoint.touchpointName.toLowerCase().replace(' ', '') ===
                touchpointName.toLowerCase() &&
              touchpoint.isIncludedInCalculations
            );
          })
      );

      Object.assign(
        touchpoints,
        getAutomaticTouchpointsData(
          includedAutomaticTouchpoints,
          calculatedBudget
        )
      );

      return getCalculatedOneReach({
        countryISO: selectedCountryISOCode,
        campaignTargetGroup: countryForm.targetGroup || '',
        touchpoints,
      });
    },
    {
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message || error.message || 'Unknown error';
        showError(errorMessage, 'info');
        setCountryOneReach(selectedCountry!, undefined);
      },
      onSuccess: (res) => {
        setCountryOneReach(selectedCountry!, res?.data.finalOneReachValue);
      },
    }
  );

  return {
    calculateOneReach,
    asyncCalculateOneReach,
    data: response?.data,
    isLoading,
    isError,
  };
};

export default useCalculateOneReach;
